import React, { useEffect, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Context menu component for positioned menus
 * Renders children (MenuItem components) at specified x, y coordinates
 * Automatically positions to stay within viewport
 *
 * Fixed: Proper portal container cleanup to prevent removeChild errors
 */
export interface ContextMenuProps {
  children: ReactNode;
  x: number;
  y: number;
  onClose: () => void;
  'data-testid'?: string;
}

export interface ContextMenuItem {
  label: string;
  icon?: ReactNode;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'danger' | 'active';
}

export function ContextMenu({ children, x, y, onClose, 'data-testid': testId }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [portalContainer] = useState(() => {
    // Create a stable portal container that won't be removed unexpectedly
    const container = document.createElement('div');
    container.setAttribute('data-portal', 'context-menu');
    return container;
  });

  // Mount and unmount portal container
  useEffect(() => {
    document.body.appendChild(portalContainer);

    return () => {
      // Safely remove portal container on unmount
      if (portalContainer.parentNode === document.body) {
        document.body.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    function handleScroll() {
      onClose();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Adjust position to stay within viewport
  useEffect(() => {
    if (menuRef.current) {
      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let adjustedX = x;
      let adjustedY = y;

      // Adjust horizontal position
      if (rect.right > viewportWidth) {
        adjustedX = viewportWidth - rect.width - 10;
      }

      // Adjust vertical position
      if (rect.bottom > viewportHeight) {
        adjustedY = viewportHeight - rect.height - 10;
      }

      // Apply adjustments
      if (adjustedX !== x || adjustedY !== y) {
        menu.style.left = `${adjustedX}px`;
        menu.style.top = `${adjustedY}px`;
      }
    }
  }, [x, y]);

  return createPortal(
    <div
      ref={menuRef}
      data-testid={testId}
      className="fixed min-w-[180px] bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 py-1"
      style={{ top: y, left: x, zIndex: 99999 }}
      role="menu"
    >
      {children}
    </div>,
    portalContainer
  );
}
